/**
 * Application-wide constants
 * Centralized location for all string literals and magic numbers
 */

// Default values
export const DEFAULTS = {
  SOFTWARE: 'xLights' as const,
  HOLIDAY: 'Christmas' as const,
  ARTIST: 'Unknown Artist' as const,
  CREATOR: 'Unknown Creator' as const,
  GENRE: 'Holiday' as const,
  ALBUM: 'Unknown Album' as const,
} as const;

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 24,
  SEARCH_PAGE_SIZE: 50,
  MAX_PAGE_SIZE: 100,
  MIN_PAGE_SIZE: 10,
} as const;

// Pricing
export const PRICING = {
  DEFAULT_SEQUENCE_PRICE: 19.99,
  MIN_PRICE: 0,
  MAX_PRICE: 999.99,
  PRICE_TIERS: {
    FREE: 0,
    BASIC: 14.99,
    STANDARD: 19.99,
    PREMIUM: 24.99,
    PROFESSIONAL: 49.99,
  },
} as const;

// Search & Sorting
export const SEARCH = {
  DEFAULT_SORT: '_text_match:desc,created_at:desc',
  SORT_OPTIONS: {
    RELEVANCE: '_text_match:desc,created_at:desc',
    // Note: Using created_at instead of download_count since that field doesn't exist in Typesense schema
    MOST_DOWNLOADED: 'created_at:desc',
    HIGHEST_RATED: 'average_rating:desc',
    NEWEST_FIRST: 'created_at:desc',
    PRICE_LOW_TO_HIGH: 'price:asc',
    PRICE_HIGH_TO_LOW: 'price:desc',
    TITLE_A_Z: 'song_title:asc',
    MOST_UPVOTED: 'upvote_count:desc,_text_match:desc',
    TRENDING: 'hot_score:desc,_text_match:desc',
  },
  DEBOUNCE_MS: 300,
  MIN_QUERY_LENGTH: 2,
} as const;

// Software Types
export const SOFTWARE_TYPES = [
  'xLights',
  'Light-O-Rama',
  'Vixen',
  'Falcon Player',
  'WLED',
  'Other',
] as const;

// Holiday Types
export const HOLIDAY_TYPES = [
  'Christmas',
  'Halloween',
  'Easter',
  'Fourth of July',
  'New Year',
  'Thanksgiving',
  'Valentine\'s Day',
  'Other',
] as const;

// Genre Types
export const GENRE_TYPES = [
  'Holiday',
  'Pop',
  'Rock',
  'Classical',
  'Country',
  'Electronic',
  'Jazz',
  'Hip Hop',
  'R&B',
  'Other',
] as const;

// File Types
export const FILE_TYPES = {
  SEQUENCE: ['.xsq', '.lms', '.vix', '.fseq', '.loredit'],
  AUDIO: ['.mp3', '.wav', '.flac', '.m4a'],
  VIDEO: ['.mp4', '.mov', '.avi', '.webm'],
  IMAGE: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
} as const;

// API Endpoints
export const API_ENDPOINTS = {
  SEQUENCES: '/api/sequences',
  DISPLAYS: '/api/displays',
  USERS: '/api/users',
  SEARCH: '/api/search',
  CHECKOUT: '/api/checkout',
} as const;

// Routes
export const ROUTES = {
  HOME: '/',
  SEQUENCES: '/sequences',
  DISPLAYS: '/displays',
  CATEGORIES: '/categories',
  PROFILE: '/profile',
  ADMIN: '/admin',
  CHECKOUT: '/checkout',
  SUCCESS: '/success',
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  GENERIC: 'An error occurred. Please try again.',
  NETWORK: 'Network error. Please check your connection.',
  AUTH_REQUIRED: 'Please sign in to continue.',
  PAYMENT_FAILED: 'Payment processing failed. Please try again.',
  UPLOAD_FAILED: 'File upload failed. Please try again.',
  SEARCH_FAILED: 'Search failed. Please try again.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  UPLOAD_COMPLETE: 'Upload completed successfully!',
  PAYMENT_COMPLETE: 'Payment processed successfully!',
  PROFILE_UPDATED: 'Profile updated successfully!',
  SEQUENCE_SAVED: 'Sequence saved successfully!',
} as const;

// Type exports for TypeScript
export type SoftwareType = typeof SOFTWARE_TYPES[number];
export type HolidayType = typeof HOLIDAY_TYPES[number];
export type GenreType = typeof GENRE_TYPES[number];